import {
  BarChartOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  ReadOutlined,
  RocketOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { MenuDataProps } from '@/common/types/components.def.ts'
import { routes, userRoutes } from '@/utils/routes.ts'
import { AllHcRoles, FinanceRole } from '@/common/types/auth.def.ts'
import { AppEnv } from '@/utils/env.ts'

export const getNavMenuData = (employeeId?: string): MenuDataProps[] => [
  {
    key: 'documentation',
    url: routes.documentation,
    label: 'Documentation',
    icon: <ReadOutlined />,
    target: '_blank',
    allowRoles: AllHcRoles,
  },
  ...(AppEnv.features.employeeProfile ? [{
    key: 'myProfile',
    url: employeeId ? userRoutes.employeeProfile(employeeId) : undefined,
    label: 'My Profile',
    icon: <UserOutlined />,
  }] : []),
  {
    key: 'document-autofill',
    url: routes.documentAutofillList,
    label: 'Document Autofill',
    icon: <FileTextOutlined />,
    allowRoles: AllHcRoles,
  },
  {
    key: 'termSheets',
    url: userRoutes.termSheets,
    label: 'Term Sheets',
    icon: <FileTextOutlined />,
    requiredPermissions: [{ moduleName: 'document-autofill', functionName: 'create-term-sheet' }],
  },
  {
    key: 'hcChecklists',
    label: 'HC Checklists',
    icon: <FileDoneOutlined />,
    allowRoles: AllHcRoles,
    children: [
      {
        key: 'hcChecklistsList',
        url: routes.hcChecklists,
        label: 'Checklists',
        allowRoles: AllHcRoles,
      },
      {
        key: 'hcChecklistsConfig',
        url: routes.hcChecklistConfig,
        label: 'Checklist Config',
        allowRoles: AllHcRoles,
      },
    ],
  },
  {
    key: 'employeeData',
    label: 'Employee Data',
    icon: <TeamOutlined />,
    allowRoles: [...AllHcRoles, FinanceRole.Operator],
    children: [
      {
        key: 'employeeDirectory',
        url: routes.employeeDirectory,
        label: 'Employee Directory',
        allowRoles: AllHcRoles,
      },
      {
        key: 'employeeDirectoryAsNonHc',
        url: userRoutes.employeeDirectory,
        label: 'Employee Directory',
        allowRoles: [FinanceRole.Operator],
      },
      ...(AppEnv.features.departmentConfig ? [{
        key: 'departmentConfig',
        url: routes.departmentConfig,
        label: 'Department Config',
        allowRoles: AllHcRoles,
      }] : []) as MenuDataProps[],
      ...(AppEnv.features.teamConfig ? [{
        key: 'teamConfig',
        url: routes.teamConfig,
        label: 'Team Config',
        allowRoles: AllHcRoles,
      }] : []) as MenuDataProps[],
    ],
  },
  ...(AppEnv.features.onboarding ? [{
    key: 'onboarding',
    label: 'Onboarding',
    icon: <CalendarOutlined />,
    url: routes.onboarding(),
    allowRoles: AllHcRoles,
  }] : []) as MenuDataProps[],
  ...(AppEnv.features.offboarding ? [{
    key: 'offboarding',
    label: 'Offboarding',
    icon: <TeamOutlined />,
    url: routes.offboardingList,
    allowRoles: AllHcRoles,
  }] : []) as MenuDataProps[],
  {
    key: 'performanceManagement',
    label: 'Perf. Mgmt',
    icon: <RocketOutlined />,
    children: [
      {
        key: 'goals',
        url: routes.appraisalGoalList,
        allowRoles: AllHcRoles,
        label: 'Team/Country Goals',
      },
      {
        key: 'appraisalEvents',
        url: routes.appraisalEventList,
        allowRoles: AllHcRoles,
        label: 'Appraisal Events',
      },
      {
        key: 'appraisals',
        url: userRoutes.appraisalResponseList,
        label: 'Appraisals',
      },
      ...(AppEnv.features.probation ? [{
        key: 'probation',
        url: userRoutes.probationList,
        label: 'Probation Reviews',
      }] : []) as MenuDataProps[],
      ...(AppEnv.features.promotion ? [{
        key: 'promotion',
        url: userRoutes.promotionList,
        managerOnly: true,
        label: 'Promotion Nominations',
      }] : []) as MenuDataProps[],
    ],
  },
  {
    key: 'headcount',
    label: 'Headcount',
    icon: <BarChartOutlined />,
    allowRoles: AllHcRoles,
    children: [
      {
        key: 'headcountReportDownload',
        label: 'Report Download',
        url: routes.headcountReports,
        allowRoles: AllHcRoles,
      },
      {
        key: 'headcountCharts',
        label: 'Charts',
        url: routes.headcountCharts,
        allowRoles: AllHcRoles,
      },
      {
        key: 'organizationChart',
        url: routes.orgChart,
        label: 'Org Chart',
        allowRoles: AllHcRoles,
      },
    ],
  },
  {
    key: 'staffCost',
    url: routes.staffCost,
    label: 'Staff Cost',
    icon: <DollarOutlined />,
    allowRoles: AllHcRoles,
  },
  {
    key: 'rewardExercise',
    url: userRoutes.rewardExercise(2026),
    label: 'Reward Exercise',
    icon: <DollarOutlined />,
    requiredPermissions: [{ moduleName: 'employee-data', functionName: 'access-reward-exercise' }],
  },
  {
    key: 'portalConfiguration',
    url: routes.portalConfig,
    label: 'Portal Configuration',
    icon: <SettingOutlined />,
    allowRoles: AllHcRoles,
  },
  {
    key: 'timeOff',
    label: 'Time Off',
    icon: <CalendarOutlined />,
    children: [
      {
        key: 'timeOffConfig',
        label: 'Time Off Config',
        url: routes.timeOffConfig,
        allowRoles: AllHcRoles,
        requiredPermissions: [{ moduleName: 'employee-data', functionName: 'manage-time-off-configuration' }],
      },
      {
        key: 'timeOffReports',
        label: 'Time Off Reports',
        url: routes.timeOffReports,
        allowRoles: AllHcRoles,
        requiredPermissions: [{ moduleName: 'employee-data', functionName: 'manage-time-off-configuration' }],
      },
      {
        key: 'employeeTimeOff',
        label: 'Individual Profile',
        url: routes.employeeTimeOff(),
        requiredPermissions: [{ moduleName: 'employee-data', functionName: 'manage-time-off-configuration' }],
      },
      {
        key: 'myTimeOff',
        label: 'My Time Off',
        url: userRoutes.myTimeOff,
      },
      {
        key: 'approval',
        label: 'Approval',
        url: userRoutes.timeOffApproval,
      },
      {
        key: 'calendar',
        label: 'Calendar',
        url: userRoutes.timeOffCalendar,
      },
    ],
  },
  ...(AppEnv.features.overtime ? [{
    key: 'overtime',
    url: userRoutes.overtimeList,
    label: 'Overtime',
    icon: <BarChartOutlined />,
  }] : []) as MenuDataProps[],
  {
    key: 'checklists',
    url: userRoutes.userChecklists(),
    label: 'Checklists',
    icon: <FileDoneOutlined />,
  },
]